import React, { useEffect, useState } from "react";
import { useWebData } from "../context/WebDataContext"; 
import { db } from "../firebaseConfig";
import { collection, addDoc, doc, deleteDoc, updateDoc } from "firebase/firestore"; 
import * as XLSX from "xlsx";
import Loader from "../components/Loader";

function PaymentDues() {
  // 🔥 FETCH DATA
  const { dues, organizations, loading } = useWebData();

  // Filter States
  const [viewMode, setViewMode] = useState('Month'); 
  const [selectedDate, setSelectedDate] = useState(new Date()); 
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear()); 
  const [searchTerm, setSearchTerm] = useState("");
  const [filtered, setFiltered] = useState([]);

  // States
  const [viewData, setViewData] = useState(null);
  const [editData, setEditData] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  
  // 🔥 Updated State: Added city & address
  const [newDue, setNewDue] = useState({ orgName: "", city: "", address: "", amount: "", billNo: "", dueDate: "", notes: "" });

  // ==========================================
  // 1. UNIVERSAL FILTER LOGIC
  // ==========================================
  useEffect(() => {
    if (!dues) return;

    let result = [...dues];

    if (searchTerm) {
        const lower = searchTerm.toLowerCase();
        result = result.filter(i => {
            const dateStr = i.dueDate ? new Date(i.dueDate).toLocaleDateString('en-GB') : "";
            const row = `${dateStr} ${i.orgName} ${i.city} ${i.amount} ${i.billNo} ${i.status}`.toLowerCase();
            return row.includes(lower);
        });
    }

    if (viewMode !== 'All') {
        const targetYear = viewMode === 'Year' ? parseInt(selectedYear) : selectedDate.getFullYear();
        const targetMonth = selectedDate.getMonth(); 

        result = result.filter(item => {
            const rawDate = item.dueDate || ""; 
            if (!rawDate) return false;
            let y = 0; let m = -1;
            const d = new Date(rawDate);
            if (!isNaN(d.getTime())) { y = d.getFullYear(); m = d.getMonth(); } 
            else {
                const parts = rawDate.split(/[-/]/);
                if (parts.length === 3) {
                    if (parts[2].length === 4) { y = parseInt(parts[2]); m = parseInt(parts[1]) - 1; } 
                    else if (parts[0].length === 4) { y = parseInt(parts[0]); m = parseInt(parts[1]) - 1; }
                }
            }
            if (viewMode === 'Year') return y === targetYear;
            if (viewMode === 'Month') return y === targetYear && m === targetMonth;
            return true;
        });
    }

    result.sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate)); 
    setFiltered(result);
  }, [dues, searchTerm, viewMode, selectedDate, selectedYear]); 

  // ==========================================
  // 2. EXCEL DOWNLOAD
  // ==========================================
  const downloadExcel = () => {
    const dataToExport = filtered.map(i => ({
        Due_Date: formatDate(i.dueDate),
        Party_Name: i.orgName,
        City: i.city || '-',
        Address: i.address || '-',
        Bill_No: i.billNo,
        Amount: i.amount,
        Status: i.status,
        Notes: i.notes,
        Created_At: formatDate(i.createdAt)
    }));

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Dues");
    XLSX.writeFile(wb, `Payment_Dues_${viewMode}.xlsx`);
  };

  // --- ACTIONS ---
  
  // 🔥 SMART ORG SELECTION (Auto-fill City/Address)
  const handleOrgSelect = (e, isEdit) => {
      const name = e.target.value;
      
      // Find matching org
      const matchedOrg = organizations.find(o => o.name === name);
      const city = matchedOrg ? matchedOrg.city : "";
      const address = matchedOrg ? `${matchedOrg.address1}, ${matchedOrg.district}` : "";

      if(isEdit) {
          setEditData({ ...editData, orgName: name, city, address });
      } else {
          setNewDue({ ...newDue, orgName: name, city, address });
      }
  };

  const handleAdd = async () => {
      if(!newDue.orgName || !newDue.amount) return alert("Party Name & Amount required");
      try {
          await addDoc(collection(db, "payment_dues"), { ...newDue, status: "Open", createdAt: new Date().toISOString() });
          alert("Due Added ✅");
          setNewDue({ orgName: "", city: "", address: "", amount: "", billNo: "", dueDate: "", notes: "" });
          setShowAddModal(false);
      } catch (e) { alert("Error adding due: " + e.message); }
  };

  const handleSaveEdit = async () => {
      if(!editData) return;
      try {
          await updateDoc(doc(db, "payment_dues", editData.id), {
              orgName: editData.orgName, 
              city: editData.city, 
              address: editData.address,
              amount: editData.amount, 
              billNo: editData.billNo,
              dueDate: editData.dueDate, 
              notes: editData.notes, 
              status: editData.status
          });
          alert("Updated ✅");
          setEditData(null);
      } catch (e) { alert("Error updating"); }
  };

  const handleDelete = async (id) => {
      if(window.confirm("Delete this record?")) await deleteDoc(doc(db, "payment_dues", id));
  };

  // Helpers
  const handleMonthChange = (dir) => {
      const d = new Date(selectedDate);
      d.setMonth(d.getMonth() + dir);
      setSelectedDate(d);
  };

  const getYearOptions = () => {
      const currentY = new Date().getFullYear();
      const years = [];
      for(let y = 2024; y <= currentY + 5; y++) years.push(y);
      return years;
  };

  const formatDate = (dateStr) => {
      if(!dateStr) return "-";
      const date = new Date(dateStr);
      return isNaN(date.getTime()) ? dateStr : date.toLocaleDateString('en-GB');
  };

  const isOverdue = (date) => new Date(date) < new Date() && new Date(date).toISOString().split('T')[0] !== new Date().toISOString().split('T')[0];

  return (
    <div style={{padding:"20px", fontFamily:"'Segoe UI', sans-serif", backgroundColor:"#f8f9fa", minHeight:"100vh"}}>
      
      {/* HEADER */}
      <div style={headerCard}>
        <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"15px"}}>
            <h2 style={{margin:0, color:"#c0392b", display:"flex", alignItems:"center"}}>
                ⏳ Payment Dues <span style={{fontSize:"14px", color:"#777", marginLeft:"10px"}}>({filtered.length})</span>
            </h2>
            <div style={{display:"flex", gap:"10px"}}>
                <button onClick={() => setShowAddModal(true)} style={btnAdd}>➕ Add Due</button>
                <button onClick={downloadExcel} style={btnDownload}>📥 Download Excel</button>
            </div>
        </div>

        <div style={{display:"flex", gap:"10px", flexWrap:"wrap", alignItems:"center", justifyContent:"space-between"}}>
            <div style={{display:"flex", gap:"5px", backgroundColor:"#eee", padding:"4px", borderRadius:"6px"}}>
                {['Month', 'Year', 'All'].map(mode => (
                    <button key={mode} onClick={()=>setViewMode(mode)} 
                        style={{...toggleBtn, backgroundColor: viewMode===mode ? "white" : "transparent", color: viewMode===mode ? "#333" : "#777", boxShadow: viewMode===mode ? "0 2px 5px rgba(0,0,0,0.1)" : "none"}}>
                        {mode}
                    </button>
                ))}
            </div>

            {viewMode === 'Month' && (
                <div style={{display:"flex", alignItems:"center", gap:"10px", backgroundColor:"white", padding:"5px 15px", borderRadius:"6px", border:"1px solid #ddd"}}>
                    <button onClick={()=>handleMonthChange(-1)} style={navBtn}>◀</button>
                    <span style={{fontWeight:"bold", color:"#333", minWidth:"120px", textAlign:"center"}}>
                        {selectedDate.toLocaleDateString('en-US', {month:'long', year:'numeric'})}
                    </span>
                    <button onClick={()=>handleMonthChange(1)} style={navBtn}>▶</button>
                </div>
            )}

            {viewMode === 'Year' && (
                <select value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)} style={{padding:"8px", borderRadius:"6px", border:"1px solid #ddd", fontWeight:"bold", fontSize:"14px", cursor:"pointer"}}>
                    {getYearOptions().map(y => <option key={y} value={y}>{y}</option>)}
                </select>
            )}

            <input type="text" placeholder="🔍 Search Party, Amount..." value={searchTerm} onChange={(e)=>setSearchTerm(e.target.value)} style={searchInput} />
        </div>
      </div>

      {loading ? <Loader /> : (
        <div style={{overflowX:"auto", backgroundColor:"white", borderRadius:"8px", boxShadow:"0 2px 10px rgba(0,0,0,0.05)"}}>
          <table style={tableStyle}>
            <thead>
                <tr style={{backgroundColor:"#c0392b", color:"white", textAlign:"left"}}>
                    <th style={thStyle}>Due Date</th>
                    <th style={thStyle}>Party Name & City</th>
                    <th style={thStyle}>Bill No</th>
                    <th style={thStyle}>Amount</th>
                    <th style={thStyle}>Status</th>
                    <th style={thStyle}>Actions</th>
                </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                  <tr><td colSpan="6" style={{padding:"20px", textAlign:"center", color:"#999"}}>No dues found.</td></tr>
              ) : filtered.map(i => (
                <tr key={i.id} style={{borderBottom:"1px solid #eee", height:"45px", backgroundColor: isOverdue(i.dueDate) && i.status!=='Collected' ? '#fff0f0' : 'white'}}>
                    <td style={{...tdStyle, color: isOverdue(i.dueDate) && i.status!=='Collected' ? 'red' : 'black'}}>
                        {formatDate(i.dueDate)}
                        {isOverdue(i.dueDate) && i.status!=='Collected' && <div style={{fontSize:"9px", fontWeight:"bold"}}>OVERDUE</div>}
                    </td>
                    
                    {/* 🔥 SHOW NAME AND CITY */}
                    <td style={{...tdStyle, textAlign:"left"}}>
                        <div style={{fontWeight:"bold"}}>{i.orgName}</div>
                        <div style={{fontSize:"10px", color:"#777"}}>{i.city}</div>
                    </td>

                    <td style={tdStyle}>{i.billNo}</td>
                    <td style={{...tdStyle, fontSize:"13px", fontWeight:"bold"}}>₹{i.amount}</td>
                    <td style={{...tdStyle, color: i.status==='Collected'?'green':'#d35400', fontWeight:"bold"}}>{i.status}</td>
                    <td style={tdStyle}>
                        <div style={{display:"flex", gap:"3px"}}>
                            <button onClick={() => setViewData(i)} style={btnIcon} title="View">👁️</button>
                            <button onClick={() => setEditData(i)} style={btnIconEdit} title="Edit">✏️</button>
                            <button onClick={() => handleDelete(i.id)} style={btnIconDelete} title="Delete">🗑️</button>
                        </div>
                    </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* VIEW MODAL */}
      {viewData && (
        <div style={modalOverlay}>
          <div style={modalContent}>
            <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"15px", borderBottom:"1px solid #eee", paddingBottom:"10px"}}>
                <h3 style={{margin:0, color:"#c0392b"}}>ℹ️ Due Details</h3>
                <button onClick={() => setViewData(null)} style={{background:"none", border:"none", fontSize:"18px", cursor:"pointer"}}>✖</button>
            </div>
            
            <div style={{fontSize:"13px", lineHeight:"1.6"}}>
                <p><strong>🏢 Party:</strong> {viewData.orgName}</p>
                <p><strong>📍 City:</strong> {viewData.city || "-"}</p>
                <p><strong>🏠 Address:</strong> {viewData.address || "-"}</p>
                <hr style={{border:"0.5px solid #eee"}}/>
                <p><strong>🧾 Bill No:</strong> {viewData.billNo}</p>
                <p><strong>💵 Amount:</strong> ₹{viewData.amount}</p>
                <p><strong>📅 Due Date:</strong> {formatDate(viewData.dueDate)}</p>
                <p><strong>🔥 Status:</strong> {viewData.status}</p>
                
                <div style={{background:"#f9f9f9", padding:"10px", marginTop:"10px", borderRadius:"5px"}}>
                    <strong>📝 Notes:</strong>
                    <p style={{margin:"5px 0 0 0"}}>{viewData.notes || 'No notes'}</p>
                </div>
            </div>
            <button onClick={()=>setViewData(null)} style={btnClose}>Close</button>
          </div>
        </div>
      )}

      {/* ADD/EDIT MODAL */}
      {(showAddModal || editData) && (
        <div style={modalOverlay}>
          <div style={modalContent}>
            <h3 style={{marginTop:0, color:"#c0392b"}}>{editData ? "✏️ Edit Due" : "➕ Add Due"}</h3>
            
            {/* 🔥🔥 SMART SEARCH DROPDOWN 🔥🔥 */}
            <label style={labelStyle}>Party Name (Select from list)</label>
            <input 
                list="org-list"
                type="text" 
                value={editData ? editData.orgName : newDue.orgName} 
                onChange={(e) => handleOrgSelect(e, !!editData)} 
                style={modalInput} 
                placeholder="Start typing name..."
            />
            {/* Datalist Source */}
            <datalist id="org-list">
                {organizations && organizations.map(org => (
                    <option key={org.id} value={org.name}>{org.city}</option>
                ))}
            </datalist>

            {/* Auto-Filled Location Display */}
            <div style={{fontSize:"11px", color:"green", marginBottom:"5px", fontWeight:"bold"}}>
                📍 Location: {editData ? (editData.city || 'Select Org') : (newDue.city || 'Select Org')}
            </div>
            
            <div style={{display:"flex", gap:"5px"}}>
                <div style={{flex:1}}>
                    <label style={labelStyle}>Amount</label>
                    <input type="text" value={editData ? editData.amount : newDue.amount} onChange={(e)=> editData ? setEditData({...editData, amount:e.target.value}) : setNewDue({...newDue, amount:e.target.value})} style={modalInput} />
                </div>
                <div style={{flex:1}}>
                    <label style={labelStyle}>Bill No</label>
                    <input type="text" value={editData ? editData.billNo : newDue.billNo} onChange={(e)=> editData ? setEditData({...editData, billNo:e.target.value}) : setNewDue({...newDue, billNo:e.target.value})} style={modalInput} />
                </div>
            </div>

            <label style={labelStyle}>Due Date</label>
            <input type="date" value={editData ? editData.dueDate : newDue.dueDate} onChange={(e)=> editData ? setEditData({...editData, dueDate:e.target.value}) : setNewDue({...newDue, dueDate:e.target.value})} style={modalInput} />

            {editData && (
                <>
                    <label style={labelStyle}>Status</label>
                    <select value={editData.status} onChange={(e)=>setEditData({...editData, status:e.target.value})} style={modalInput}>
                        <option>Open</option>
                        <option>Collected</option>
                    </select>
                </>
            )}

            <label style={labelStyle}>Notes</label>
            <textarea value={editData ? editData.notes : newDue.notes} onChange={(e)=> editData ? setEditData({...editData, notes:e.target.value}) : setNewDue({...newDue, notes:e.target.value})} style={{...modalInput, height:"60px"}}></textarea>

            <div style={{marginTop:"15px", textAlign:"right"}}>
                <button onClick={()=> { setShowAddModal(false); setEditData(null); }} style={btnCancel}>Cancel</button>
                <button onClick={editData ? handleSaveEdit : handleAdd} style={btnAdd}>{editData ? "Update" : "Save"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// STYLES
const headerCard={backgroundColor:"white", padding:"20px", borderRadius:"10px", boxShadow:"0 2px 8px rgba(0,0,0,0.05)", marginBottom:"20px"};
const tableStyle={width:"100%", borderCollapse:"collapse", fontSize:"13px"};
const thStyle={padding:"12px 15px", borderBottom:"2px solid #eee", fontSize:"12px", textTransform:"uppercase", letterSpacing:"0.5px"};
const tdStyle={padding:"10px 15px", verticalAlign:"middle"};
const toggleBtn={border:"none", padding:"6px 12px", borderRadius:"5px", cursor:"pointer", fontSize:"13px", fontWeight:"600", transition:"0.2s"};
const navBtn={background:"none", border:"none", cursor:"pointer", fontSize:"16px", color:"#555"};
const searchInput={padding:"8px 12px", border:"1px solid #ddd", borderRadius:"6px", width:"200px", outline:"none"};
const btnDownload={backgroundColor:"#27ae60", color:"white", border:"none", padding:"8px 15px", borderRadius:"5px", cursor:"pointer", fontWeight:"bold", fontSize:"13px"};
const btnAdd={backgroundColor:"#c0392b", color:"white", border:"none", padding:"8px 15px", borderRadius:"5px", cursor:"pointer", fontWeight:"bold", fontSize:"13px", marginRight:"10px"};

const btnIcon={backgroundColor:"#3498db", color:"white", border:"none", padding:"5px 8px", borderRadius:"4px", cursor:"pointer", marginRight:"5px"};
const btnIconEdit={backgroundColor:"#f39c12", color:"white", border:"none", padding:"5px 8px", borderRadius:"4px", cursor:"pointer", marginRight:"5px"};
const btnIconDelete={backgroundColor:"#fff", color:"#777", border:"1px solid #ddd", padding:"5px 8px", borderRadius:"4px", cursor:"pointer"};

const modalOverlay={position:"fixed", top:0, left:0, width:"100%", height:"100%", background:"rgba(0,0,0,0.5)", display:"flex", justifyContent:"center", alignItems:"center", zIndex:1000};
const modalContent={background:"white", padding:"25px", borderRadius:"12px", width:"400px", boxShadow:"0 10px 30px rgba(0,0,0,0.2)", position:"relative"};
const modalInput={width:"100%", padding:"8px", margin:"5px 0", borderRadius:"4px", border:"1px solid #ccc", boxSizing:"border-box", fontSize:"13px"};
const labelStyle={fontSize:"11px", fontWeight:"bold", color:"#555"};
const btnCancel={backgroundColor:"#95a5a6", color:"white", border:"none", padding:"8px 15px", borderRadius:"4px", cursor:"pointer", marginRight:"5px"};
const btnClose={marginTop:"15px", backgroundColor:"#333", color:"white", border:"none", padding:"8px", borderRadius:"4px", width:"100%", cursor:"pointer"};

export default PaymentDues;